<script setup lang="ts">
  defineOptions({
    name: 'AuthLayout',
  })

  import { computed } from 'vue'

  import { useHead } from 'nuxt/app'
  import { useLocation } from '@common/location-layer/composables/useLocation'

  import { buildHeroPreloadLinks, resolveAuthBackgroundStyle, resolvePortalHeroImage } from '@clientportal/auth-layer/utils/branding-assets'

  const { store: locationStore } = useLocation()

  // Split shell only when the tenant has a portal image; otherwise fall back to
  // the centered-card + brand-gradient shell (no generic bundled art).
  const heroImage = computed(() => resolvePortalHeroImage(locationStore.branding))

  const hasPortalImage = computed(() => Boolean(heroImage.value))

  // encodeURI so a branding value with `"` / `)` cannot break out of url() and inject CSS.
  const heroStyle = computed(() => ({ backgroundImage: `url("${encodeURI(heroImage.value ?? '')}")` }))

  // Fallback shell background: brand gradient, else the default gradient token.
  const backgroundStyle = computed(() => ({ backgroundImage: resolveAuthBackgroundStyle(locationStore.branding) }))

  // Preload the hero as the desktop LCP (>= lg only, so mobile never fetches it).
  useHead({ link: () => buildHeroPreloadLinks(heroImage.value) })
</script>

<template>
  <!-- Split shell: contained form left, full-bleed portal art right (art hidden below lg). -->
  <div v-if="hasPortalImage" class="grid min-h-[100dvh] w-full bg-background lg:grid-cols-[1fr_1.05fr]">
    <!-- Form panel: padded; the form container fills height so lockup pins top and footer pins bottom. -->
    <section class="flex flex-col px-6 py-11 md:px-10 md:py-14">
      <div class="mx-auto flex w-full max-w-[25rem] flex-1 flex-col justify-center">
        <slot />
      </div>
    </section>

    <!-- Art panel: full-bleed, no rounding/margin, subtle inset vignette. -->
    <aside id="cp-auth-hero" class="relative hidden overflow-hidden bg-cover bg-center lg:block" :style="heroStyle" aria-hidden="true">
      <div class="pointer-events-none absolute inset-0" style="box-shadow: inset 0 0 120px rgba(0, 0, 0, 0.35)" />
    </aside>
  </div>

  <!-- Fallback shell: centered card on a brand-color gradient background. -->
  <div v-else id="cp-auth-background" class="auth-layout-background" :style="backgroundStyle">
    <div class="auth-card-container">
      <div id="cp-auth-card" class="auth-card bg-background">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .auth-layout-background {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
  }

  .auth-card-container {
    width: 100%;
    max-width: 28rem;
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 10;
  }

  .auth-card {
    width: 100%;
    padding: 1rem;
    border-radius: 0.75rem;
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    position: relative;
    overflow: hidden;
  }

  @media (max-width: 640px) {
    .auth-layout-background {
      padding: 1rem;
    }

    .auth-card {
      padding: 1.5rem;
    }
  }
</style>
